import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useCurrentPractice } from '../contexts/PracticeContext'
import { isAdminUser, isScratchAdminUser, isStaff } from '../util/user'
import { selectContext } from '../selectors/auth'

/**
 * Hook to determine if refunds should be disabled for users
 *
 * @returns {boolean} disableRefundsForStaff - true if refunds should be disabled for the current staff user
 */
export const useRefundsPermission = (): { disableRefundsForStaff: boolean } => {
	const { currentPracticeObject, currentConsolidator } = useCurrentPractice()
	const context: Context = useSelector(selectContext)
	const currentUser = context?.user as any

	const disableRefundsForStaff = useMemo(() => {
		// if the current user is a scratch admin or admin, allow refunds
		if (isScratchAdminUser(currentUser) || isAdminUser(currentUser)) {
			return false
		}

		// check if the current user is a staff user
		if (isStaff(currentUser, currentPracticeObject?.id as string)) {
			// Check practice level flag first - if it's explicitly set (true or false), use it
			const practiceFlag = currentPracticeObject?.settings?.restrictPaymentsRefundsForStaffUser
			if (typeof practiceFlag !== 'undefined') {
				return practiceFlag
			}

			// If practice flag is undefined, check consolidator level flag
			const consolidatorFlag =
				currentConsolidator?.featureFlags?.restrictPaymentsRefundsForStaffUser
			return consolidatorFlag === true
		}

		return false
	}, [currentPracticeObject, currentConsolidator, currentUser])

	return { disableRefundsForStaff }
}
